// Starter Tape — /starter-tape/tape/:tapeId. One lesson: read the brief,
// make a short track inside its guardrails, then "finish" it to hear your
// choices named back (listen-back) and save it to the Tape Shelf. Meeting
// the tape's must unlocks the next tape; the take saves either way.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  arpeggiate,
  chordInBar,
  diatonicChord,
  setAttack,
  setBarChord,
  setSectionTrack,
} from "../lib/starterTape";
import {
  PATHS,
  getPath,
  getTape,
  nextTape,
  tapeLengthLabel,
  tapeSong,
} from "../lib/starterTapeCurriculum";
import { checkMust, listenBack } from "../lib/starterTapeListen";
import {
  isUnlocked,
  nextTakeTitle,
  saveTake,
} from "../lib/starterTapeProgress";
import ArpPanel from "../components/starterTape/ArpPanel";
import ArrangementGrid from "../components/starterTape/ArrangementGrid";
import ChordStrip from "../components/starterTape/ChordStrip";
import Studio from "../components/starterTape/Studio";
import TapeShell from "../components/starterTape/TapeShell";
import useTapePlayer from "../components/starterTape/useTapePlayer";
import {
  loadDraft,
  loadProgress,
  saveDraft,
  saveProgress,
} from "../components/starterTape/storage";

const SWELLS = [
  { id: "slow", label: "Slow swell", attack: 1.2 },
  { id: "fast", label: "Fast swell", attack: 0.02 },
];

const NOTE_LABELS = {
  did: "What you did",
  why: "Why it works",
  nudge: "Try next",
};

export default function StarterTapeTape() {
  const { tapeId } = useParams();
  const tape = getTape(tapeId);
  const [progress, setProgress] = useState(loadProgress);

  if (!tape) {
    return (
      <TapeShell title="No such tape">
        <Link to="/starter-tape" className="text-amber-300 hover:underline">
          Back to the tapes
        </Link>
      </TapeShell>
    );
  }

  if (!isUnlocked(progress, tape.id)) {
    return (
      <TapeShell eyebrow={`Tape ${tape.number}`} title={tape.title}>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 text-zinc-300">
          <p>This tape opens when you finish the one before it.</p>
          <Link
            to="/starter-tape"
            className="mt-4 inline-block rounded-lg bg-amber-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-amber-200"
          >
            Back to the tapes
          </Link>
        </div>
      </TapeShell>
    );
  }

  // Keyed so switching tapes (e.g. "Next tape") starts fresh state.
  return (
    <TapeSession
      key={tape.id}
      tape={tape}
      progress={progress}
      setProgress={setProgress}
    />
  );
}

// The newest saved take of the tape this one builds on that met its must.
const sourceTake = (progress, tape) =>
  tape.setup.fromTape
    ? progress.shelf.find((t) => t.tapeId === tape.setup.fromTape && t.met) ||
      null
    : null;

function TapeSession({ tape, progress, setProgress }) {
  const source = sourceTake(progress, tape);
  const freshSong = () => tapeSong(tape, source && source.song);
  // A draft saved before a tape gained a lane gets that lane added, filled
  // the way a fresh start would fill it.
  const [song, setSong] = useState(() => {
    const draft = loadDraft(tape.id);
    if (!draft) return freshSong();
    const fresh = freshSong();
    const missing = fresh.tracks.filter(
      (t) => !draft.tracks.some((d) => d.id === t.id)
    );
    if (missing.length === 0) return draft;
    return {
      ...draft,
      tracks: fresh.tracks.map(
        (t) => draft.tracks.find((d) => d.id === t.id) || t
      ),
    };
  });
  const [review, setReview] = useState(null); // listen-back result
  const [arpPattern, setArpPattern] = useState("upDown");
  const [saved, setSaved] = useState(null); // { title, met }
  const player = useTapePlayer(song);
  const next = nextTape(tape.id);

  useEffect(() => saveDraft(tape.id, song), [tape.id, song]);
  useEffect(() => saveProgress(progress), [progress]);

  const must = useMemo(() => checkMust(tape.must.id, song), [tape, song]);
  const pad = song.tracks.find((t) => t.instrument === "pad");
  const swell = pad && pad.attack < 0.5 ? "fast" : "slow";

  const edit = (s) => {
    setSong(s);
    setReview(null);
    setSaved(null);
  };

  const finish = () => {
    setReview(listenBack(tape, song));
    setSaved(null);
  };

  const save = () => {
    const title = nextTakeTitle(progress, tape);
    const met = checkMust(tape.must.id, song).met;
    setProgress(
      saveTake(progress, {
        id: `take-${Date.now()}`,
        tapeId: tape.id,
        title,
        song,
        met,
        savedAt: Date.now(),
      })
    );
    setSaved({ title, met });
  };

  const startOver = () => edit(freshSong());

  const previewChord = (index, variant = null) => {
    if (player.playing) return;
    const chord = diatonicChord(song, index, undefined, variant);
    if (chord) chord.pitches.forEach((p) => player.preview("pad", p));
  };

  const pickChord = (bar, index) => {
    edit(setBarChord(song, "pad", bar, index));
    if (index !== null) previewChord(index);
  };

  // Turn the chord already in `bar` into a sus chord (or back, for null).
  const pickVariant = (bar, variant) => {
    const current = chordInBar(song, "pad", bar);
    if (!current) return;
    edit(setBarChord(song, "pad", bar, current.index, variant));
    previewChord(current.index, variant);
  };

  const has = (control) => tape.controls.includes(control);
  const soundControls = tape.controls.filter(
    (c) => c === "space" || c === "tone"
  );
  const path = getPath(tape.side);
  const fromTitle = tape.setup.fromTape
    ? (getTape(tape.setup.fromTape) || {}).title
    : null;
  const firstPathTape = PATHS[0] && getTape(PATHS[0].tapes[0].id);

  return (
    <TapeShell
      eyebrow={`${path ? path.name : "Side A"} · Tape ${tape.number} · ${tapeLengthLabel(tape)}`}
      theme={path ? path.theme : "studio"}
      title={tape.title}
      subtitle={tape.mood}
    >
      {/* Brief */}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
          <p className="text-xs uppercase tracking-widest text-zinc-500">
            Brief
          </p>
          <dl className="mt-2 space-y-2 text-sm">
            <div>
              <dt className="inline text-zinc-500">You have: </dt>
              <dd className="inline text-zinc-200">{tape.palette}</dd>
            </div>
            {tape.setup.fromTape && (
              <p className="text-xs text-zinc-500">
                {source
                  ? tape.setup.carry
                    ? `Keeps the bell melody from your saved “${source.title}”.`
                    : `Starts from your saved “${source.title}”.`
                  : tape.setup.carry
                    ? `Uses a sample bell melody. Save a ${fromTitle} take that meets its must to hear your own.`
                    : `Starts from a sample loop. Save a ${fromTitle} take that meets its must to start from your own.`}
              </p>
            )}
            <div className="flex items-start gap-2">
              <span
                className={cn(
                  "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]",
                  must.met
                    ? "border-emerald-400 bg-emerald-400/20 text-emerald-300"
                    : "border-zinc-600 text-transparent"
                )}
                aria-hidden="true"
              >
                ✓
              </span>
              <span>
                <span className="text-zinc-500">One must: </span>
                <span className="text-zinc-200">{tape.must.text}</span>
                <span className="sr-only">
                  {must.met ? " (done)" : " (not yet)"}
                </span>
              </span>
            </div>
          </dl>
          <p className="mt-3 text-xs uppercase tracking-widest text-zinc-500">
            Tries (optional)
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-zinc-300">
            {tape.tries.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-300/30 bg-amber-300/5 p-4">
          <p className="text-xs uppercase tracking-widest text-amber-300/80">
            Idea
          </p>
          <p className="mt-1 font-semibold text-zinc-100">{tape.idea.title}</p>
          <p className="mt-2 text-sm leading-relaxed text-zinc-300">
            {tape.idea.body}
          </p>
        </div>
      </div>

      <Studio
        song={song}
        setSong={edit}
        player={player}
        showTempo={tape.controls.includes("tempo")}
        soundControls={soundControls}
        beforeLanes={
          <>
            {has("sections") && (
              <div className="mb-4">
                <ArrangementGrid
                  song={song}
                  onToggle={(sectionId, trackId, on) =>
                    edit(setSectionTrack(song, sectionId, trackId, on))
                  }
                />
              </div>
            )}
            {has("chords") && (
              <ChordStrip
                song={song}
                trackId="pad"
                chords={tape.chords}
                onPick={pickChord}
                variants={has("sus")}
                onVariant={pickVariant}
              />
            )}
            {has("arp") && (
              <div className="mb-4">
                <ArpPanel
                  pattern={arpPattern}
                  onPatternChange={setArpPattern}
                  onArpeggiate={() =>
                    edit(arpeggiate(song, "pad", "bell", arpPattern, 1))
                  }
                  disabled={!song.tracks.some((t) => t.id === "bell")}
                />
              </div>
            )}
          </>
        }
        transportExtras={
          <>
            {tape.controls.includes("swell") && pad && (
              <div
                className="flex rounded-lg border border-zinc-700 p-0.5"
                role="group"
                aria-label="Pad swell"
              >
                {SWELLS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => edit(setAttack(song, pad.id, s.attack))}
                    aria-pressed={swell === s.id}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-sm",
                      swell === s.id
                        ? "bg-zinc-100 text-zinc-900"
                        : "text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={startOver}
              className="rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              Start over
            </button>
          </>
        }
      />

      {/* Finish + listen-back */}
      <div className="mt-6">
        {!review ? (
          <button
            type="button"
            onClick={finish}
            disabled={song.tracks.every((t) => t.notes.length === 0)}
            className="rounded-lg bg-amber-300 px-5 py-3 font-semibold text-zinc-900 hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Finish tape
          </button>
        ) : (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900 p-5">
            <p className="text-xs uppercase tracking-widest text-zinc-500">
              Listen back
            </p>
            <ul className="mt-3 space-y-3">
              {review.notes.map((n) => (
                <li key={n.kind + n.text}>
                  <p
                    className={cn(
                      "text-xs font-medium",
                      n.kind === "why" ? "text-amber-300" : "text-zinc-500"
                    )}
                  >
                    {NOTE_LABELS[n.kind]}
                  </p>
                  <p className="text-zinc-200">{n.text}</p>
                </li>
              ))}
            </ul>

            {!saved ? (
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={save}
                  className="rounded-lg bg-zinc-100 px-4 py-2 font-semibold text-zinc-900 hover:bg-white"
                >
                  Save to shelf
                </button>
                <button
                  type="button"
                  onClick={() => setReview(null)}
                  className="rounded-lg border border-zinc-700 px-4 py-2 text-zinc-300 hover:bg-zinc-800"
                >
                  Keep working
                </button>
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <p className="text-sm text-emerald-300">
                  Saved “{saved.title}” to your Tape Shelf.
                </p>
                {saved.met ? (
                  <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/5 p-4">
                    <p className="font-semibold text-zinc-100">Unlocked</p>
                    <ul className="mt-1 list-disc pl-5 text-sm text-zinc-300">
                      {tape.unlocks.map((u) => (
                        <li key={u}>{u}</li>
                      ))}
                    </ul>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {next ? (
                        <Link
                          to={`/starter-tape/tape/${next.id}`}
                          className="rounded-lg bg-amber-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-amber-200"
                        >
                          Next: Tape {next.number}, {next.title} →
                        </Link>
                      ) : tape.side === "sideA" && firstPathTape ? (
                        <Link
                          to={`/starter-tape/tape/${firstPathTape.id}`}
                          className="rounded-lg bg-indigo-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-indigo-200"
                        >
                          Start the {PATHS[0].name} path →
                        </Link>
                      ) : tape.side === "deepSpace" ? (
                        <Link
                          to="/starter-tape/sandbox/deep-space"
                          className="rounded-lg bg-indigo-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-indigo-200"
                        >
                          Open the Deep Space sandbox →
                        </Link>
                      ) : (
                        <Link
                          to="/starter-tape/sandbox"
                          className="rounded-lg bg-amber-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-amber-200"
                        >
                          Open the sandbox →
                        </Link>
                      )}
                      <Link
                        to="/starter-tape"
                        className="rounded-lg border border-zinc-700 px-4 py-2 text-zinc-300 hover:bg-zinc-800"
                      >
                        Back to the tapes
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">
                    The next tape opens once the must is met: {tape.must.text}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </TapeShell>
  );
}
